/**
 * usePushNotifications
 * -------------------------------------------------------------------
 * Gerencia ciclo de vida de subscriptions Web Push:
 *  - registro de service worker
 *  - solicitação de permissão
 *  - subscribe/unsubscribe no PushManager
 *  - sincronização com tabela `push_subscriptions` no Supabase
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext.hook";
import {
  canPromptForPermission,
  detectPlatform,
  isPushSupported,
  isStandalone,
  urlB64ToUint8Array,
  type PushPlatform,
} from "@/utils/pushUtils";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

type PermissionState = "default" | "granted" | "denied" | "unsupported";

export type PromptResponse = "accepted" | "rejected" | null;

export interface PushDevice {
  id: string;
  endpoint: string;
  device_label: string | null;
  user_agent: string | null;
  created_at: string;
  last_used_at: string;
}

/**
 * Categorias persistidas em notification_preferences. Quando o usuário
 * responde ao banner inicial via `recordPromptDecision`, todas as flags
 * são marcadas conforme a decisão (aceitar=true, recusar=false).
 */
type CategoryFlags = {
  push_enabled: boolean;
  push_chat: boolean;
  push_status: boolean;
  push_document: boolean;
  push_admin_broadcast: boolean;
};

function readPermission(): PermissionState {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission as PermissionState;
}

function subscriptionKeys(sub: PushSubscription): { p256dh: string; auth: string } {
  // toJSON já codifica em urlsafe-base64 — formato esperado pelo web-push no backend
  const json = sub.toJSON() as { keys?: { p256dh?: string; auth?: string } };
  return {
    p256dh: json.keys?.p256dh ?? "",
    auth: json.keys?.auth ?? "",
  };
}

function buildDeviceLabel(platform: PushPlatform): string {
  const pretty: Record<PushPlatform, string> = {
    ios: "iPhone/iPad",
    android: "Android",
    desktop: "Desktop",
  };
  return `${pretty[platform]} — ${new Date().toLocaleDateString("pt-BR")}`;
}

/**
 * Aguarda o SW ficar pronto com timeout. Sem timeout, `serviceWorker.ready`
 * fica pendente para sempre se a registração falhou — bloqueia subscribe()
 * em "Ativando..." indefinidamente.
 */
async function waitForServiceWorker(timeoutMs = 10_000): Promise<ServiceWorkerRegistration> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`Service Worker não respondeu em ${timeoutMs}ms`)),
        timeoutMs,
      ),
    ),
  ]);
}

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [permission, setPermission] = useState<PermissionState>(readPermission());
  // Em dev (vite dev server) o SW não é registrado pelo PushBootstrap, então
  // qualquer await em serviceWorker.ready trava no timeout de 10s. Trata o
  // hook como "não suportado" para evitar warnings ruidosos no console e o
  // banner de "Ativar notificações" aparecer fora de produção.
  const [supported] = useState<boolean>(
    import.meta.env.PROD && isPushSupported() && !!VAPID_PUBLIC_KEY,
  );
  const [standalone, setStandalone] = useState<boolean>(isStandalone());
  const [platform] = useState<PushPlatform>(detectPlatform());
  const [isBusy, setIsBusy] = useState(false);
  const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null);
  const [promptResponse, setPromptResponse] = useState<PromptResponse>(null);
  const [promptResponseLoaded, setPromptResponseLoaded] = useState(false);

  // Monitor display-mode changes (ex.: usuário instala PWA no meio da sessão)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia?.("(display-mode: standalone)");
    if (!mq) return;
    const listener = () => setStandalone(isStandalone());
    mq.addEventListener?.("change", listener);
    return () => mq.removeEventListener?.("change", listener);
  }, []);

  // Lê a subscription atual do navegador (se houver) para saber o endpoint ativo.
  // IMPORTANTE: usa `serviceWorker.ready` em vez de `getRegistration()`. O SW
  // do Workbox registra de forma assíncrona em PushBootstrap; se lermos antes
  // dele estar pronto, getRegistration() retorna undefined e currentEndpoint
  // fica permanentemente null — o que faz o banner "Ativar notificações"
  // reaparecer mesmo quando o usuário já tem subscription válida.
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await waitForServiceWorker(10_000);
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setCurrentEndpoint(sub?.endpoint ?? null);
      } catch (err) {
        console.warn("getSubscription falhou:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported]);

  // Self-heal: garante que o DB tenha uma row ativa para este endpoint
  // ligada ao user atual. Trata 3 cenários:
  //   (a) Upsert anterior falhou silenciosamente → DB sem row para o endpoint
  //       → cria row.
  //   (b) Row existe mas pertence a outro user_id (ex.: troca de login no
  //       mesmo device) → o user atual não consegue ver a row (RLS), upsert
  //       por endpoint fica bloqueado → force-reset: unsubscribe browser e
  //       subscribe de novo para gerar endpoint limpo.
  //   (c) Row já existe para o user atual → noop.
  useEffect(() => {
    if (!supported || !user || !currentEndpoint) return;
    let cancelled = false;
    (async () => {
      try {
        // Cheque estrito: existe row para este endpoint ASSINADO PELO USER?
        const { data: mine } = await supabase
          .from("push_subscriptions" as never)
          .select("id")
          .eq("endpoint", currentEndpoint)
          .eq("user_id", user.id)
          .is("revoked_at", null)
          .maybeSingle();
        if (cancelled || mine) return;

        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        if (cancelled || !sub || sub.endpoint !== currentEndpoint) return;

        const { p256dh, auth } = subscriptionKeys(sub);
        if (!p256dh || !auth) return;

        const row = {
          user_id: user.id,
          endpoint: sub.endpoint,
          p256dh,
          auth,
          user_agent: navigator.userAgent,
          device_label: buildDeviceLabel(platform),
          last_used_at: new Date().toISOString(),
          revoked_at: null as string | null,
        };

        const { error } = await supabase
          .from("push_subscriptions" as never)
          .upsert(row as never, { onConflict: "endpoint" });

        if (!error) return;

        // Upsert falhou — provavelmente endpoint UNIQUE pertence a outro user
        // e RLS bloqueou UPDATE. Reset: descarta subscription do browser e
        // cria nova (gera endpoint diferente, INSERT limpo).
        console.warn(
          "push subscription pertence a outro user, resetando:",
          error,
        );

        try {
          await sub.unsubscribe();
        } catch (unsubErr) {
          console.warn("falha ao unsubscribe no reset:", unsubErr);
        }

        if (cancelled) return;
        // Limpa state local — force o efeito de leitura a reler
        setCurrentEndpoint(null);

        // Pede nova subscription com o mesmo VAPID — browser gera endpoint
        // novo. Essa chamada fará INSERT limpo na tabela.
        if (!VAPID_PUBLIC_KEY) return;
        try {
          const newSub = await reg!.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlB64ToUint8Array(
              VAPID_PUBLIC_KEY,
            ) as unknown as BufferSource,
          });
          if (cancelled) return;

          const { p256dh: p2, auth: a2 } = subscriptionKeys(newSub);
          if (!p2 || !a2) return;

          const newRow = {
            user_id: user.id,
            endpoint: newSub.endpoint,
            p256dh: p2,
            auth: a2,
            user_agent: navigator.userAgent,
            device_label: buildDeviceLabel(platform),
            last_used_at: new Date().toISOString(),
            revoked_at: null as string | null,
          };

          const { error: insErr } = await supabase
            .from("push_subscriptions" as never)
            .upsert(newRow as never, { onConflict: "endpoint" });
          if (insErr) {
            console.error("falha ao inserir nova subscription:", insErr);
            return;
          }

          setCurrentEndpoint(newSub.endpoint);
        } catch (subErr) {
          console.error("falha ao recriar subscription:", subErr);
        }
      } catch (err) {
        console.error("push subscription self-heal exception:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported, user, currentEndpoint, platform]);

  // ------------------------------------------------------------------
  // Carrega prompt_response do DB para decidir se o banner aparece.
  // É a fonte de verdade cross-device — substitui o fallback local
  // baseado em localStorage, que reaparecia a cada 3 dias.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!user) {
      setPromptResponse(null);
      setPromptResponseLoaded(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("notification_preferences" as never)
        .select("prompt_response")
        .eq("user_id", user.id)
        .maybeSingle();

      if (cancelled) return;
      if (error && error.code !== "PGRST116") {
        console.warn("Falha ao ler prompt_response:", error);
      }
      const resp = (data as { prompt_response?: PromptResponse } | null)
        ?.prompt_response ?? null;
      setPromptResponse(resp);
      setPromptResponseLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Persiste decisão do banner no DB. Aceitar liga master + categorias;
  // recusar desliga tudo. Usuário pode refinar depois em PushSettings.
  const recordPromptDecision = useCallback(
    async (decision: "accepted" | "rejected"): Promise<boolean> => {
      if (!user) return false;
      const enabled = decision === "accepted";
      const flags: CategoryFlags = {
        push_enabled: enabled,
        push_chat: enabled,
        push_status: enabled,
        push_document: enabled,
        push_admin_broadcast: enabled,
      };
      const row = {
        user_id: user.id,
        ...flags,
        prompt_response: decision,
        prompt_responded_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from("notification_preferences" as never)
        .upsert(row as never, { onConflict: "user_id" });
      if (error) {
        console.error("recordPromptDecision falhou:", error);
        return false;
      }
      setPromptResponse(decision);
      return true;
    },
    [user],
  );

  // Subscribe: pede permissão (se necessário), gera subscription, persiste no banco
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!supported || !user) return false;
    if (!VAPID_PUBLIC_KEY) {
      console.warn("VITE_VAPID_PUBLIC_KEY não configurada.");
      return false;
    }
    if (!canPromptForPermission(platform)) {
      // iOS sem PWA instalada — não adianta pedir
      return false;
    }

    setIsBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm as PermissionState);
      if (perm !== "granted") return false;

      const reg = await waitForServiceWorker(10_000);

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          // Cast necessário: Uint8Array<ArrayBufferLike> vs BufferSource no lib DOM atual
          applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as BufferSource,
        });
      }

      const { p256dh, auth } = subscriptionKeys(sub);
      if (!p256dh || !auth) {
        console.error("Subscription sem chaves — abortando");
        return false;
      }

      // Upsert por endpoint (mesma fonte de verdade do browser)
      const row = {
        user_id: user.id,
        endpoint: sub.endpoint,
        p256dh,
        auth,
        user_agent: navigator.userAgent,
        device_label: buildDeviceLabel(platform),
        last_used_at: new Date().toISOString(),
        revoked_at: null as string | null,
      };

      const { error } = await supabase
        .from("push_subscriptions" as never)
        .upsert(row as never, { onConflict: "endpoint" });

      if (error) {
        console.error("Erro persistindo subscription:", error);
        return false;
      }

      setCurrentEndpoint(sub.endpoint);
      return true;
    } catch (err) {
      console.error("Falha ao subscrever push:", err);
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [supported, user, platform]);

  // Unsubscribe: desconecta do navegador e marca como revoked no banco
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;
    setIsBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      const endpoint = sub?.endpoint ?? currentEndpoint;

      if (sub) {
        await sub.unsubscribe();
      }

      if (endpoint) {
        await supabase
          .from("push_subscriptions" as never)
          .update({ revoked_at: new Date().toISOString() } as never)
          .eq("endpoint", endpoint);
      }

      setCurrentEndpoint(null);
      return true;
    } catch (err) {
      console.error("Falha ao desinscrever push:", err);
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [supported, currentEndpoint]);

  // Lista todos os dispositivos ativos do usuário (para tela de configurações)
  const listDevices = useCallback(async (): Promise<PushDevice[]> => {
    if (!user) return [];
    const { data, error } = await supabase
      .from("push_subscriptions" as never)
      .select("id, endpoint, device_label, user_agent, created_at, last_used_at")
      .eq("user_id", user.id)
      .is("revoked_at", null)
      .order("last_used_at", { ascending: false });

    if (error) {
      console.error("listDevices falhou:", error);
      return [];
    }
    return (data ?? []) as unknown as PushDevice[];
  }, [user]);

  // Revoga por id (útil na tela de dispositivos)
  const revokeDevice = useCallback(async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from("push_subscriptions" as never)
      .update({ revoked_at: new Date().toISOString() } as never)
      .eq("id", id);
    if (error) {
      console.error("revokeDevice falhou:", error);
      return false;
    }
    return true;
  }, []);

  // canPromptNow: além de plataforma capaz, exige
  //  - permission ainda em "default" (já granted/denied não precisa de banner)
  //  - prompt_response carregado do DB e ainda NULL
  // Se DB não carregou ainda, retorna false para evitar flash do banner antes
  // de saber se o usuário já respondeu.
  const canPromptNow =
    canPromptForPermission(platform) &&
    permission === "default" &&
    promptResponseLoaded &&
    promptResponse === null;

  return {
    supported,
    permission,
    platform,
    standalone,
    canPromptNow,
    promptResponse,
    promptResponseLoaded,
    isSubscribed: !!currentEndpoint && permission === "granted",
    isBusy,
    subscribe,
    unsubscribe,
    listDevices,
    revokeDevice,
    recordPromptDecision,
  };
};
